/**
 * Photos and videos on appointment comments and client notes — the shard's API (boona:
 * app/api/v1/endpoints/attachments.py, docs/codebase/attachments/README.md §8). Photos go up in one
 * request; videos in pieces (5 MB) that resume after a dropped connection, then the server converts them.
 * File links are short-lived and signed; they are relative to the regional API (resolveMediaUrl).
 */
import { apiClient } from "@/lib/apiClient"

export type UploadTarget =
  | { kind: "record"; recordId: number }
  | { kind: "note"; noteId: number }
  | { kind: "note_draft"; clientId: number }

export interface ApiAttachment {
  id: number
  kind: "photo" | "video"
  status: "uploading" | "processing" | "ready" | "failed"
  url: string | null
  download_url: string | null
  thumb_url: string | null
  width: number | null
  height: number | null
  duration: number | null
  size: number
  by: string | null
  created_at: string
  can_delete: boolean
}

export interface ApiNote {
  id: number
  text: string
  author: string
  mine: boolean
  imported: boolean
  noted_at: string | null
  changed_at: string | null
  can_edit: boolean
  attachments: ApiAttachment[]
}

const MULTIPART = { "Content-Type": "multipart/form-data" } // axios lets the browser add the boundary

function photoPath(t: UploadTarget): string {
  if (t.kind === "record") return `/api/v1/records/${t.recordId}/attachments`
  if (t.kind === "note") return `/api/v1/client-notes/${t.noteId}/attachments`
  return `/api/v1/clients/${t.clientId}/note-uploads`
}

export async function listRecordAttachments(recordId: number): Promise<ApiAttachment[]> {
  const r = await apiClient.get(`/api/v1/records/${recordId}/attachments`)
  return r.data?.items ?? []
}

export async function uploadPhoto(target: UploadTarget, file: File, onProgress: (p: number) => void,
                                  signal?: AbortSignal): Promise<ApiAttachment> {
  const body = new FormData()
  body.append("file", file, file.name)
  const r = await apiClient.post(photoPath(target), body, {
    headers: MULTIPART, signal, timeout: 180_000,
    onUploadProgress: (e) => onProgress(Math.min(1, e.loaded / (e.total || file.size || 1))),
  })
  return r.data.item
}

export async function startVideo(target: UploadTarget, file: File): Promise<{ upload_id: number; chunk_size: number; received: number }> {
  const where = target.kind === "record" ? { record_id: target.recordId }
    : target.kind === "note" ? { note_id: target.noteId } : { client_id: target.clientId }
  const r = await apiClient.post("/api/v1/attachments/uploads", { target: target.kind, size: file.size, name: file.name, ...where })
  return r.data
}

export async function putPiece(uploadId: number, offset: number, piece: Blob, onProgress: (loaded: number) => void,
                               signal?: AbortSignal): Promise<{ received: number; status: string }> {
  const r = await apiClient.put(`/api/v1/attachments/uploads/${uploadId}`, piece, {
    params: { offset }, headers: { "Content-Type": "application/octet-stream" }, signal, timeout: 180_000,
    onUploadProgress: (e) => onProgress(e.loaded),
  })
  return r.data
}

export async function uploadState(uploadId: number): Promise<{ received: number; status: string; item?: ApiAttachment }> {
  const r = await apiClient.get(`/api/v1/attachments/uploads/${uploadId}`)
  return r.data
}

export async function deleteAttachment(id: number): Promise<void> {
  await apiClient.delete(`/api/v1/attachments/${id}`)
}

export async function listClientNotes(clientId: number): Promise<{ notes: ApiNote[]; can_add: boolean }> {
  const r = await apiClient.get(`/api/v1/clients/${clientId}/notes`)
  return r.data
}

export async function addClientNote(clientId: number, text: string, attachmentIds: number[]): Promise<{ id: number }> {
  const r = await apiClient.post(`/api/v1/clients/${clientId}/notes`, { text, attachment_ids: attachmentIds })
  return r.data
}

/** The sentence the server sent with a refusal ({status: "error", code, error}), if any */
export function refusal(err: any): { code?: string; message?: string; status?: number } {
  const data = err?.response?.data
  return { code: data?.code, message: typeof data?.error === "string" ? data.error : undefined, status: err?.response?.status }
}
